import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { apiCommon } from 'src/api/ApiCommon';
import { FormInputState, FormModel, group, Result, ResultValue } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import {
    validateExpectPositiveNumber,
    validateRequire,
    validateRequireFile,
    validateStringLength30,
} from 'src/api/utils/formValidators';
import { CsvReadFile } from 'src/AppState/PromotionQuestionsState/PromotionQuestionsState';
import Papa from 'papaparse';
import { selectFile } from 'src/utils/selectFile';
import { decodePromoCodeObjects, PromoCodesType } from 'src/AppState/PromoCodesState/commonCmsGuard';
import { PromoCodeModelType } from 'src/api/config/promo_codes/get_promo_codes';
import moment from 'moment';
interface PromoCodeLoading {
    type: 'loading';
}
interface PromoCodeReady {
    type: 'ready';
    value: Array<PromoCodesType>;
}
interface PromoCodeError {
    type: 'error';
    value: string;
}

type PromoCodeContentResponse = PromoCodeLoading | PromoCodeReady | PromoCodeError;
type PageType = 'edit' | 'add';

interface PromoCodeType {
    date_from: string;
    date_to: string;
    number_of_uses: number | null;
    promo_code: string;
    affiliate_id: string | null;
}

export interface PromoCodeElementType {
    date_from: string;
    date_to: string;
    number_of_uses: number | null | undefined;
    promo_code: PromoCodeInnerType;
    id: number;
    affiliate_id: string | null | undefined;
}
interface PromoCodeSubmitType {
    date_from: string;
    date_to: string;
    number_of_uses: number | null | undefined;
    promo_code: Array<PromoCodeInnerType>;
    affiliate_id: string | null;
}
interface PromoCodeValidType {
    date_from: string;
    date_to: string;
    number_of_uses: null | undefined | number;
    promo_code: string;
    affiliate_id: string | null;
}
export interface PromoCodeInnerType {
    code: string;
    used: number;
}

export class PromoCodesState {
    @observable private readonly promoCodesResource: Resource<Array<PromoCodeModelType>>;
    @observable private page: string = 'add';
    @observable private isActiveFlagChanged: boolean = false;
    @observable public editedPromoCodeElement: PromoCodeElementType | null = null;
    @observable public editedCode: string | null = null;
    @observable public currentPromoCode: PromoCodeModelType | null = null;
    @observable public isLoading: boolean = false;
    @observable public errorMessage: string = '';
    @observable public duplicateError: string | null = null;
    @observable public dateError: string | null = null;
    @observable public limitError: string | null = null;
    @observable public isUnlimited: boolean = false;

    readonly dateFromModel: FormModel<string>;
    readonly date_from: FormInputState<string>;
    readonly dateFromStatus: InputStatus;
    readonly dateToModel: FormModel<string>;
    readonly date_to: FormInputState<string>;
    readonly dateToStatus: InputStatus;
    readonly form: FormModel<PromoCodeValidType>;
    readonly numberOfUsesModel: FormModel<number | null>;
    readonly number_of_uses: FormInputState<string>;
    readonly numberOfUsesStatus: InputStatus;
    readonly promoCodeModel: FormModel<string>;
    readonly promo_code: FormInputState<string>;
    readonly promoCodeStatus: InputStatus;
    readonly affiliateIdModel: FormModel<string | null>;
    readonly affiliate_id: FormInputState<string>;
    readonly affiliateIdStatus: InputStatus;

    readonly allowedMimeTypes: string[] = ['text/csv'];
    readonly allowedFileTypes: string[] = ['.csv'];

    @observable promoFileName: string | null = null;
    @observable csvFile: CsvReadFile | null = null;

    readonly file: FormInputState<boolean>;
    readonly fileModel: FormModel<boolean>;
    readonly fileStatus: InputStatus;

    constructor() {
        this.file = new FormInputState(false, false);
        this.fileModel = this.file.map(validateRequireFile);
        this.fileStatus = new InputStatus(this.fileModel);

        this.promoCodesResource = new Resource(async (): Promise<Array<PromoCodeModelType>> => {
            this.isLoading = true;
            const response = await apiCommon.get_promo_codes.run({});
            this.isLoading = false;
            return response;
        });

        this.date_from = new FormInputState('', false);
        this.dateFromModel = this.date_from.map(validateRequire);
        this.dateFromStatus = new InputStatus(this.dateFromModel);

        this.date_to = new FormInputState('', false);
        this.dateToModel = this.date_to.map(validateRequire);
        this.dateToStatus = new InputStatus(this.dateToModel);

        this.promo_code = new FormInputState('', false);
        this.promoCodeModel = this.promo_code.map(validateStringLength30);
        this.promoCodeStatus = new InputStatus(this.promoCodeModel);

        this.number_of_uses = new FormInputState('', false);
        this.numberOfUsesModel = this.number_of_uses.map(validateExpectPositiveNumber);
        this.numberOfUsesStatus = new InputStatus(this.numberOfUsesModel);

        this.affiliate_id = new FormInputState('', false);
        this.affiliateIdModel = this.affiliate_id.map((value: string): Result<string> => {
            return new ResultValue(value);
        });
        this.affiliateIdStatus = new InputStatus(this.affiliateIdModel);

        this.form = group({
            date_from: this.dateFromModel,
            date_to: this.dateToModel,
            number_of_uses: this.numberOfUsesModel,
            promo_code: this.promoCodeModel,
            affiliate_id: this.affiliateIdModel,
        }).map((item: PromoCodeType): Result<PromoCodeValidType> => {
            return new ResultValue({
                date_from: item.date_from,
                date_to: item.date_to,
                number_of_uses: item.number_of_uses === null ? null : Number(item.number_of_uses),
                promo_code: item.promo_code,
                affiliate_id: item.affiliate_id,
            });
        });
        makeAutoObservable(this);
    }

    @action public handleCancel = (): void => {
        if (this.isEditMode && this.editedPromoCodeElement !== null) {
            this.setEditedPromoCode(this.editedPromoCodeElement);
        } else {
            this.form.reset();
        }
    };

    @computed get isFormValid(): boolean {
        return this.form.result instanceof ResultValue;
    }

    @action handleSelectFile = async (files: File[]): Promise<void> => {
        const file = files[0];
        //@ts-expect-error
        this.promoFileName = file.name;
        try {
            //@ts-expect-error
            const parsedFile = await this.parseCSV(file);
            if (parsedFile.length > 0) {
                this.file.setValue(true);
                //@ts-expect-error
                this.csvFile = new CsvReadFile(file.name, Date.now().toString(), parsedFile.flat());
            } else {
                throw new Error('File is corrupted, unreadable or empty.');
            }
        } catch (error) {
            console.log(error);
        }
        this.promoFileName = null;
    };

    private parseCSV = async (file: File): Promise<string[]> => {
        return new Promise((resolve, reject) => {
            Papa.parse<string>(file, {
                error: (error) => {
                    reject(error.message);
                },
                complete: (result) => {
                    resolve(result.data);
                },
            });
        });
    };

    @action handleRemoveFile = (): void => {
        this.file.setValue(false);
        this.promoFileName = null;
        this.csvFile = null;
    };

    @computed public get addedPromoCodes(): Array<string> | null {
        return this.csvFile === null ? null : this.csvFile.possibleAnswers.filter(this.onlyUniqueCodes);
    }

    @action handleReplaceFile = async (event: React.SyntheticEvent): Promise<void> => {
        this.preventDefaults(event);
        const filesList = await selectFile({ acceptFileType: this.allowedFileTypes.join(',') });
        const files = Array.from(filesList);
        if (files.length > 0) {
            this.handleRemoveFile();
            await this.handleSelectFile(files);
        }
    };

    private preventDefaults = (event: React.SyntheticEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    @action public resetForm = (): void => {
        this.setPage('add');
        this.editedPromoCodeElement = null;
        this.editedCode = null;
        this.csvFile = null;
        this.isUnlimited = false;
        this.duplicateError = null;
        this.dateError = null;
        this.limitError = null;
        this.form.reset();
    };

    @action public setPage = (value: PageType): void => {
        this.page = value;
    };

    @action public onRemove = async (promoCode: PromoCodeElementType): Promise<void> => {
        const id = promoCode.id;
        const code = promoCode.promo_code.code;
        const promoCodeListItem = this.promoCodeList.find((e) => e.id === id);

        if (promoCodeListItem) {
            const promoCodesInner = promoCodeListItem.promo_code.filter((singleCode) => singleCode.code !== code);

            const willrecordBeEmpty =
                promoCodeListItem.promo_code.length === 1 &&
                promoCodeListItem.promo_code.find((singleCode) => singleCode.code === code) !== undefined;
            const removeResult = willrecordBeEmpty
                ? await apiCommon.delete_promo_code.run({ id })
                : await apiCommon.put_update_promo_code.run({
                      data: { ...promoCodeListItem, promo_code: promoCodesInner },
                      id: id,
                  });

            if (removeResult) {
                await this.promoCodesResource.refresh();

                this.form.reset();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }
    };

    @action public onUpdateRemove = async (id: number, code: string): Promise<void> => {
        const promoCodeListItem = this.promoCodeList.find((e) => e.id === id);
        const promoCodesInner = promoCodeListItem?.promo_code.filter((singleCode) => singleCode.code !== code);
        if (promoCodeListItem) {
            const removeResult = await apiCommon.put_update_promo_code.run({
                data: { ...promoCodeListItem, promo_code: promoCodesInner },
                id: id,
            });

            if (removeResult) {
                await this.promoCodesResource.refresh();

                this.form.reset();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }
    };
    public onlyUniqueCodes = (value: string, index: number, self: Array<string>): boolean => {
        return self.indexOf(value) === index;
    };

    public promoCodesFromFile = () => {
        if (this.addedPromoCodes !== null) {
            return this.addedPromoCodes.map((data: string) => {
                return {
                    code: data,
                    used: 0,
                };
            });
        }
        return null;
    };

    public checkIfDuplicate = (codesInner: Array<PromoCodeInnerType>): boolean => {
        for (const promoCode of this.promoCodeList) {
            for (const singleCodeObject of promoCode.promo_code) {
                for (const codeToAdd of codesInner) {
                    if (singleCodeObject.code === codeToAdd.code) {
                        return true;
                    }
                }
            }
        }
        return false;
    };

    @action public handleSubmit = async () => {
        this.isLoading = true;
        this.errorMessage = '';
        this.form.setAsVisited();
        const result = this.form.result;

        if (result instanceof ResultValue && this.promoCodes.type === 'ready') {
            const codeToUpdate = result.value.promo_code;
            const codesFromFile = this.promoCodesFromFile();
            const codeFromField = {
                code: result.value.promo_code,
                used: 0,
            };

            const payload: PromoCodeSubmitType = {
                date_from: result.value.date_from,
                date_to: result.value.date_to,
                number_of_uses: result.value.number_of_uses,
                promo_code:
                    codesFromFile === null
                        ? [codeFromField]
                        : codeFromField.code !== ''
                          ? [...codesFromFile, codeFromField]
                          : codesFromFile,
                affiliate_id: result.value.affiliate_id === '' ? null : result.value.affiliate_id,
            };

            const submitResult =
                this.isEditMode && this.editedPromoCodeElement !== null && this.editedCode !== null
                    ? await this.updatePromoCode(payload, this.editedPromoCodeElement.id, this.editedCode, codeToUpdate)
                    : await this.createPromoCode(payload);

            if (submitResult) {
                await this.promoCodesResource.refresh();
                this.resetForm();
            } else {
                if (this.limitError === null && this.duplicateError === null && this.dateError === null) {
                    this.errorMessage = 'Unknown error. Something went wrong.';
                }
            }
        }

        this.isLoading = false;
    };

    @action public numberOfUsesIsInValid = (number_of_uses: number | null | undefined): boolean => {
        const allNotFilled = (number_of_uses === undefined || number_of_uses === null) && this.isUnlimited === false;
        const allFilled = number_of_uses !== null && number_of_uses !== undefined && this.isUnlimited === true;

        if (allNotFilled || allFilled) {
            return true;
        }
        return false;
    };

    public isDateValid = (dateFrom: string, dateTo: string): boolean => {
        return moment(dateTo).isAfter(dateFrom);
    };

    @action private createPromoCode = async (promoCode: PromoCodeSubmitType): Promise<number | Error | null> => {
        let response: number | Error | null;

        try {
            const isDuplicate = this.checkIfDuplicate(promoCode.promo_code);
            if (isDuplicate) {
                this.duplicateError = 'There is existing promo code with the same name.';
                return null;
            }
            const isNumberOfUsesSet = this.numberOfUsesIsInValid(promoCode.number_of_uses);
            if (isNumberOfUsesSet) {
                this.limitError = 'Please insert number of uses or select unlimited option';
                return null;
            }
            const checkIfDatesAreValid = this.isDateValid(promoCode.date_from, promoCode.date_to);
            if (checkIfDatesAreValid === false) {
                this.dateError = 'Please provide valid date range';
                return null;
            }
            response = await apiCommon.post_create_promo_code.run({ data: promoCode });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action private updatePromoCode = async (
        promoCode: PromoCodeSubmitType,
        id: number,
        editedCode: string,
        codeToUpdate: string
    ): Promise<number | Error | null> => {
        let response: number | Error | null;
        const promoCodeListItem = this.promoCodeList.find((e) => e.id === id);

        const isDuplicate = codeToUpdate !== editedCode && this.checkIfDuplicate(promoCode.promo_code);
        if (isDuplicate) {
            this.duplicateError = 'There is existing promo code with the same name.';
            return null;
        }

        const isNumberOfUsesSet = this.numberOfUsesIsInValid(promoCode.number_of_uses);
        if (isNumberOfUsesSet) {
            this.limitError = 'Please insert number of uses or select unlimited option';
            return null;
        }

        const checkIfDatesAreValid = this.isDateValid(promoCode.date_from, promoCode.date_to);
        if (checkIfDatesAreValid === false) {
            this.dateError = 'Please provide valid date range';
            return null;
        }

        if (promoCodeListItem !== undefined && promoCodeListItem.promo_code.length === 1) {
            try {
                const updatedCode = promoCodeListItem.promo_code.find((singleCode) => singleCode.code === editedCode);
                response = await apiCommon.put_update_promo_code.run({
                    data: { ...promoCode, promo_code: [{ code: codeToUpdate, used: updatedCode?.used }] },
                    id: id,
                });
            } catch (error) {
                console.error(error);
                return null;
            }
        } else if (promoCodeListItem !== undefined) {
            try {
                const updatedCode = promoCodeListItem.promo_code.find((singleCode) => singleCode.code === editedCode);

                response = await apiCommon.post_create_promo_code.run({
                    data: { ...promoCode, promo_code: [{ code: codeToUpdate, used: updatedCode?.used }] },
                });
                await this.onUpdateRemove(id, editedCode);
            } catch (error) {
                console.error(error);
                return null;
            }
        } else {
            return null;
        }

        return response;
    };

    @action public setEditedPromoCode = (promoCode: PromoCodeElementType): void => {
        this.editedPromoCodeElement = promoCode;
        this.editedCode = promoCode.promo_code.code;
        this.csvFile = null;
        if (promoCode.number_of_uses === null || promoCode.number_of_uses === undefined) {
            this.isUnlimited = true;
        }

        if (this.promoCodes.type === 'ready') {
            const editedCode = promoCode.promo_code;

            this.number_of_uses.setValue(promoCode.number_of_uses ? promoCode.number_of_uses.toString() : '');
            this.promo_code.setValue(editedCode.code ?? '');
            this.date_from.setValue(promoCode.date_from);
            this.date_to.setValue(promoCode.date_to);
            this.affiliate_id.setValue(promoCode.affiliate_id ?? '');
        }
    };

    @computed private get promoCodes(): PromoCodeContentResponse {
        const promoCode = this.promoCodesResource.get();
        if (promoCode.type === 'ready' && promoCode.value !== null) {
            const promoCodeObject = decodePromoCodeObjects(promoCode.value);

            return {
                type: 'ready',
                value: promoCodeObject,
            };
        }
        if (promoCode.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching promo codes info',
        };
    }

    public checkIfIsActive = (promoCode: PromoCodeElementType | PromoCodesType): boolean => {
        const startDate = moment(promoCode.date_from);
        const endDate = moment(promoCode.date_to);
        const currentDate = moment();
        const isDateAfter = moment(startDate).isAfter(currentDate);
        const isDateBefore = moment(endDate).isBefore(currentDate);

        return isDateAfter === false && isDateBefore === false;
    };

    @computed public get promoCodeList(): Array<PromoCodesType> {
        if (this.promoCodes.type === 'ready') {
            return this.promoCodes.value;
        }
        return [];
    }

    @computed public get validPromoCodeList(): Array<PromoCodesType> {
        return this.promoCodeList.filter((p) => this.checkIfIsActive(p));
    }

    @computed public get promoCodesElementsList(): Array<PromoCodeElementType> {
        const promoCodesElements: Array<PromoCodeElementType> = [];
        for (const promoCode of this.promoCodeList) {
            if (promoCode.promo_code.length > 1) {
                for (const singleCode of promoCode.promo_code) {
                    const validElement = {
                        date_from: promoCode.date_from,
                        date_to: promoCode.date_to,
                        number_of_uses: promoCode.number_of_uses ?? null,
                        promo_code: singleCode,
                        id: promoCode.id,
                        affiliate_id: promoCode.affiliate_id,
                    };
                    promoCodesElements.push(validElement);
                }
            } else if (promoCode.promo_code.length === 1 && promoCode.promo_code[0] !== undefined) {
                const validElement = {
                    date_from: promoCode.date_from,
                    date_to: promoCode.date_to,
                    number_of_uses: promoCode.number_of_uses ?? null,
                    promo_code: promoCode.promo_code[0],
                    id: promoCode.id,
                    affiliate_id: promoCode.affiliate_id,
                };
                promoCodesElements.push(validElement);
            }
        }
        return promoCodesElements.sort((a: PromoCodeElementType, b: PromoCodeElementType) => {
            const isAValid = this.checkIfIsActive(a);
            const isBValid = this.checkIfIsActive(b);
            return isAValid === isBValid
                ? moment(a.date_to) < moment(b.date_to)
                    ? -1
                    : moment(a.date_to) > moment(b.date_to)
                      ? 1
                      : b.promo_code.used - a.promo_code.used
                : isAValid
                  ? -1
                  : 1;
        });
    }

    @computed public get isEditMode(): boolean {
        return this.page === 'edit' || this.isActiveFlagChanged;
    }

    @computed private get parsedStartDate(): number | null {
        return this.date_from.value !== null ? Date.parse(this.date_from.value) : null;
    }

    @computed private get parsedEndDate(): number | null {
        return this.date_to.value !== null ? Date.parse(this.date_to.value) : null;
    }

    @computed public get startDate(): Date | null {
        return this.parsedStartDate === null || isNaN(this.parsedStartDate) ? null : new Date(this.parsedStartDate);
    }

    @computed public get endDate(): Date | null {
        return this.parsedEndDate === null || isNaN(this.parsedEndDate) ? null : new Date(this.parsedEndDate);
    }

    static createForContext(): PromoCodesState {
        return new PromoCodesState();
    }
}
